import { useEffect, useState } from "react";
import { Modal, Form, Select, InputNumber, Switch, Button, Row, Col, Input, message } from "antd";
import {
  get_available_ai_providers,
  get_available_ai_features,
} from "../../api/admin.apis";

const { TextArea } = Input;

const { Option } = Select;

const toFeatureLabel = (value) =>
  value
    .split("_")
    .map((word) => (word.length ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");

const CreateAiConfigModal = ({ visible, loading, onSubmit, onClose }) => {
  const [form] = Form.useForm();
  const [providers, setProviders] = useState({});
  const [features, setFeatures] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    setOptionsLoading(true);

    Promise.all([get_available_ai_providers(), get_available_ai_features()])
      .then(([providersRes, featuresRes]) => {
        if (cancelled) return;
        setProviders(providersRes?.data?.data?.providers || {});
        setFeatures(featuresRes?.data?.data?.features || []);
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Failed to load AI providers and features");
      })
      .finally(() => {
        if (cancelled) return;
        setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    form.setFieldsValue({ model_version: undefined });
  };

  const handleFinish = (values) => {
    // We always render with the LLM in this admin panel; the deterministic
    // Python renderer is not exposed. Any row created here is pinned to the
    // LLM path on the production endpoint.
    onSubmit({ ...values, use_deterministic_renderer: false });
  };

  const handleCancel = () => {
    onClose();
  };

  const handleAfterClose = () => {
    form.resetFields();
    setSelectedProvider(null);
  };

  const providerEntries = Object.entries(providers);
  const featureOptions = features.map((f) =>
    typeof f === "string"
      ? { value: f, label: toFeatureLabel(f) }
      : { value: f.value ?? f.name, label: f.label ?? toFeatureLabel(f.value ?? f.name) }
  );
  const modelOptions = selectedProvider
    ? providers[selectedProvider]?.models || []
    : [];

  return (
    <Modal
      title="Add New AI Configuration"
      open={visible}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      footer={null}
      destroyOnClose
      width={600}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          label="Feature Name"
          name="feature_name"
          rules={[{ required: true, message: "Feature name is required" }]}
        >
          <Select placeholder="Select feature" loading={optionsLoading}>
            {featureOptions.map((f) => (
              <Option key={f.value} value={f.value}>{f.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Provider"
          name="provider"
          rules={[{ required: true, message: "Provider is required" }]}
        >
          <Select placeholder="Select provider" onChange={handleProviderChange} loading={optionsLoading}>
            {providerEntries.map(([value, info]) => (
              <Option key={value} value={value}>{info?.name || value}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Model Version"
          name="model_version"
          rules={[{ required: true, message: "Model version is required" }]}
        >
          <Select placeholder={selectedProvider ? "Select model" : "Select a provider first"} disabled={!selectedProvider}>
            {modelOptions.map((m) => (
              <Option key={m} value={m}>{m}</Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Temperature" name="temperature" initialValue={0.7}>
              <InputNumber min={0} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Max Tokens" name="max_tokens" initialValue={4000}>
              <InputNumber min={0} max={128000} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Top P" name="top_p" initialValue={0}>
              <InputNumber min={0} max={1} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Frequency Penalty" name="frequency_penalty" initialValue={0}>
              <InputNumber min={-2} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Presence Penalty" name="presence_penalty" initialValue={0}>
              <InputNumber min={-2} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="English System Prompt"
          name="en_system_prompt"
          extra={<><strong>Note:</strong> Must wrap the prompt with <code>"""</code> (three double quotes) at the start and end. Must maintain the example structure of the prompt.</>}
        >
          <TextArea rows={3} placeholder="Enter English system prompt..." />
        </Form.Item>

        <Form.Item
          label="English User Prompt"
          name="en_user_prompt"
          extra={<><strong>Note:</strong> Must wrap the prompt with <code>"""</code> (three double quotes) at the start and end. Must maintain the example structure of the prompt.</>}
        >
          <TextArea rows={3} placeholder="Enter English user prompt..." />
        </Form.Item>

        <Form.Item
          label="Arabic System Prompt"
          name="ar_system_prompt"
          extra={<><strong>Note:</strong> Must wrap the prompt with <code>"""</code> (three double quotes) at the start and end. Must maintain the example structure of the prompt.</>}
        >
          <TextArea rows={3} placeholder="Enter Arabic system prompt..." dir="rtl" />
        </Form.Item>

        <Form.Item
          label="Arabic User Prompt"
          name="ar_user_prompt"
          extra={<><strong>Note:</strong> Must wrap the prompt with <code>"""</code> (three double quotes) at the start and end. Must maintain the example structure of the prompt.</>}
        >
          <TextArea rows={3} placeholder="Enter Arabic user prompt..." dir="rtl" />
        </Form.Item>

        <Form.Item label="Enabled" name="enabled" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Create Configuration
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateAiConfigModal;
